import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import Sheet from '../../shared/ui/Sheet';
import { Toggle } from '../../shared/ui';
import { updateUserProfile } from '../../shared/services/userService';

// iOS DriverSettingsView navigation-app options (exact list).
export const NAVIGATION_APPS = ['Apple Maps', 'Google Maps'];

// Mirrors iOS DriverSettingsView: App Preferences toggles + Navigation App
// picker + About CheF. iOS persists these via @AppStorage (keys
// deliveryNotifications / earningsNotifications / navigationApp); on web they
// are stored on the users/{uid} doc so they sync across devices.
export default function SettingsSheet({ open, onClose, uid, profile, onSaved }) {
  const [deliveryNotifications, setDeliveryNotifications] = useState(true);
  const [earningsNotifications, setEarningsNotifications] = useState(true);
  const [navApp, setNavApp] = useState('Apple Maps');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setDeliveryNotifications(profile?.deliveryNotifications !== false);
      setEarningsNotifications(profile?.earningsNotifications !== false);
      setNavApp(profile?.preferredNavigationApp || profile?.navigationApp || 'Apple Maps');
      setError('');
    }
  }, [open, profile]);

  async function persist(fields) {
    if (!uid) return;
    try {
      await updateUserProfile(uid, fields);
      onSaved?.(fields);
    } catch (e) {
      console.error('settings save', e);
      setError('Failed to save setting. Please try again.');
    }
  }

  const toggle = (key, value, setter) => {
    setter(value);
    persist({ [key]: value });
  };

  return (
    <Sheet open={open} onClose={onClose} title="Settings">
      <div className="px-4 pb-6 space-y-5">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
            App Preferences
          </p>
          <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm font-medium text-gray-800">Delivery Updates</span>
              <Toggle
                checked={deliveryNotifications}
                onChange={v => toggle('deliveryNotifications', v, setDeliveryNotifications)}
              />
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm font-medium text-gray-800">Earnings Updates</span>
              <Toggle
                checked={earningsNotifications}
                onChange={v => toggle('earningsNotifications', v, setEarningsNotifications)}
              />
            </div>
          </div>
        </div>

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
            Navigation App
          </p>
          <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
            {NAVIGATION_APPS.map(app => (
              <button
                key={app}
                type="button"
                onClick={() => {
                  setNavApp(app);
                  persist({ preferredNavigationApp: app });
                }}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-800 active:bg-gray-50"
              >
                {app}
                {navApp === app && <Check size={17} className="text-[#FF6B35]" />}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-red-500">{error}</p>}

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
            About CheF
          </p>
          <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm font-medium text-gray-800">App Version</span>
              <span className="text-sm text-gray-400">1.0.0</span>
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm font-medium text-gray-800">Operated by</span>
              <span className="text-sm text-gray-400">Browne Ink</span>
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm font-medium text-gray-800">Support</span>
              <a href="tel:+18696601052" className="text-sm text-[#FF6B35] font-medium">
                +1 (869) 660-1052
              </a>
            </div>
          </div>
        </div>
      </div>
    </Sheet>
  );
}
